import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CommaInput from './CommaInput';
import {
  Card,
  SectionTitle,
  SectionTitleRow,
  SectionSubText,
  SectionContent,
  FormRow,
  FormFieldGroup,
  FormFieldLabel,
  FormFieldDesc,
  Divider,
  ToggleSwitch,
  ToggleFieldRow,
  FieldBlock,
  RadioRow,
  RadioItem,
  RadioCircle,
  RadioDot,
  RadioLabel,
  LessonLabel,
  OverflowRow,
  OverflowLabel,
  InputUnit,
  RangeRow,
  RangeInputBox,
  RangeSeparator,
  AddRangeButton,
  AddRangeText,
  UnitSelect,
  UnitSuffixBox,
  RealInput,
  InputBoxText,
  NoteText,
  CheckRow,
  CheckLabel,
  DisabledNote,
  ErrorFieldWrapper,
  ErrorText,
  HelpTooltip,
  EmptyRangeHint,
} from './SalarySettingStyled';

// ============================================
// 초과 매출/수업 수당 섹션
// ============================================
const OverflowSettingItem = ({
  title,
  desc,
  enabled,
  onToggle,
  vatIncluded,
  onVatChange,
  basis,
  onBasisChange,
  countValue,
  onCountChange,
  amountValue,
  onAmountChange,
  validationError,
  countFieldName,
  amountFieldName,
}) => {
  const { t } = useTranslation();

  return (
    <FieldBlock>
      <FormRow sx={{ alignItems: 'flex-start' }}>
        <FormFieldGroup>
          <ToggleFieldRow>
            <FormFieldLabel>{title}</FormFieldLabel>
            <ToggleSwitch checked={enabled === 'Y'} onChange={onToggle} />
          </ToggleFieldRow>
          <FormFieldDesc>{desc}</FormFieldDesc>
        </FormFieldGroup>

        {enabled === 'Y' && (
          <FieldBlock sx={{ flex: 1, gap: '16px' }}>
            {/* 판정(초과 여부)은 기본/개별 수당 지급액 누적으로 그대로 하고, 여기서 정한
                부가세 설정은 "초과매출수당"으로 지급되는 차액 자체에만 적용한다
                (2026-09-20, 사용자 확정 — 각 수당은 자기 값에만 부가세를 건다). */}
            <RadioRow sx={{ justifyContent: 'flex-start' }}>
              <LessonLabel>{t('부가세')}</LessonLabel>
              <RadioItem onClick={() => onVatChange('Y')}>
                <RadioCircle isSelected={vatIncluded === 'Y'}>
                  {vatIncluded === 'Y' && <RadioDot />}
                </RadioCircle>
                <RadioLabel>{t('포함')}</RadioLabel>
              </RadioItem>
              <RadioItem onClick={() => onVatChange('N')}>
                <RadioCircle isSelected={vatIncluded !== 'Y'}>
                  {vatIncluded !== 'Y' && <RadioDot />}
                </RadioCircle>
                <RadioLabel>{t('미포함')}</RadioLabel>
              </RadioItem>
            </RadioRow>
            <NoteText>
              {t('※ 초과매출수당으로 실제 지급되는 금액(차액)에만 이 부가세 설정이 적용됩니다. 기준 초과 여부 판정(누적)에는 영향을 주지 않습니다.')}
              <br />
              {t('※ "원" 기준일 때만 적용되며, "회" 기준은 매출 개념이 없어 이 설정과 무관합니다.')}
            </NoteText>
            <Divider />

            <OverflowRow>
              <OverflowLabel>{t('월')}</OverflowLabel>
              <ErrorFieldWrapper hasError={validationError?.field === countFieldName} data-field={countFieldName}>
                <CommaInput inputWidth="140px" value={countValue} onChange={onCountChange} />
              </ErrorFieldWrapper>
              {/* 높이 44px·라운드 8px 은 옆의 금액 입력칸(RealInput)과 같은 규격이다.
                  피그마(7149:1481)에도 같은 규격으로 그려져 있어 인라인 크기 지정을 두지 않는다. */}
              <UnitSelect value={basis || 'AMOUNT'} onChange={e => onBasisChange(e.target.value)}>
                <option value="AMOUNT">원</option>
                <option value="COUNT">회</option>
              </UnitSelect>
              <OverflowLabel>{t('초과 시 회당')}</OverflowLabel>
              <ErrorFieldWrapper hasError={validationError?.field === amountFieldName} data-field={amountFieldName}>
                <CommaInput inputWidth="140px" value={amountValue} onChange={onAmountChange} />
              </ErrorFieldWrapper>
              <UnitSuffixBox>{t('원')}</UnitSuffixBox>
              <OverflowLabel>{t('으로 수당 변경')}</OverflowLabel>
            </OverflowRow>
            {validationError?.field === countFieldName && <ErrorText>{validationError.message}</ErrorText>}
            {validationError?.field === amountFieldName && <ErrorText>{validationError.message}</ErrorText>}
            <NoteText>
              {t('※ 기준을 넘긴 수업부터 회당 수당이 위 금액으로 바뀝니다. 이전 수업에는 소급되지 않습니다.')}
              <br />
              {t('※ "원"은 진행한 수업의 기본 수당 지급액 누적, "회"는 수당이 발생한 수업 건수 기준입니다.')}
            </NoteText>
          </FieldBlock>
        )}
      </FormRow>
    </FieldBlock>
  );
};

const SalarySettingOverflow = ({ settings, onSettingsChange, validationError }) => {
  const { t } = useTranslation();
  const toggle = key => onSettingsChange(key, settings[key] === 'Y' ? 'N' : 'Y');

  return (
    <Card>
      <SectionTitleRow>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SectionTitle>{t('초과 매출/수업 수당')}</SectionTitle>
          <HelpTooltip title={"기준을 넘긴 수업부터 회당 수당이 설정한 금액으로 바뀝니다(소득세 누진구간과 같은 방식). 기준을 넘기기 전 수업에는 소급되지 않습니다.\n\n원(지급액 기준): 진행한 수업의 기본 수당 지급액을 날짜순으로 누적해 설정 금액을 넘긴 수업부터 적용\n회(건수 기준): 수당이 발생한 수업 건수가 설정 횟수를 넘긴 수업부터 적용"} />
        </Box>
        <SectionSubText>{t('※ 수업 수당을 설정한 이후에 사용이 가능합니다')}</SectionSubText>
      </SectionTitleRow>

      <SectionContent sx={{ mt: '24px' }}>
        <OverflowSettingItem
          title={t('개인레슨 초과 매출/수업 수당')}
          desc={t('기본 수당 지급액/횟수가 설정한 수치보다 높을 때 기본 수당 변경')}
          enabled={settings.privateOverflowEnabled}
          onToggle={() => toggle('privateOverflowEnabled')}
          vatIncluded={settings.privateOverflowVatIncluded}
          onVatChange={val => onSettingsChange('privateOverflowVatIncluded', val)}
          basis={settings.privateOverflowBasis}
          onBasisChange={val => onSettingsChange('privateOverflowBasis', val)}
          countValue={settings.privateOverflowCount}
          onCountChange={val => onSettingsChange('privateOverflowCount', val)}
          amountValue={settings.privateOverflowAmount}
          onAmountChange={val => onSettingsChange('privateOverflowAmount', val)}
          validationError={validationError}
          countFieldName="privateOverflowCount"
          amountFieldName="privateOverflowAmount"
        />
      </SectionContent>
    </Card>
  );
};

// ============================================
// 성과급 산정 대상 체크박스 옵션
// ============================================
const BONUS_TARGET_OPTIONS = [
  { value: 'ALL', label: '전체' },
  { value: 'DAY_PASS', label: '일일권' },
  { value: 'MEMBERSHIP', label: '이용권' },
  { value: 'PRIVATE_TICKET', label: '수강권(개인)' },
  { value: 'GROUP_TICKET', label: '수강권(그룹)' },
  { value: 'PRODUCT', label: '일반상품' },
];

// ============================================
// 성과급 섹션
// ============================================
const BonusSettingItem = ({
  title,
  desc,
  enabledKey,
  typeKey,
  thresholdKey,
  amountKey,
  vatKey,
  basisKey,
  targetsKey,
  bonusCategory,
  settings,
  onSettingsChange,
  validationError,
}) => {
  const { t } = useTranslation();
  const enabled = settings[enabledKey] === 'Y';
  const settlementType = settings[typeKey] || 'FIXED';
  const vatIncluded = settings[vatKey] || 'N';
  const basis = settings[basisKey] || 'SALE_PRICE';
  const targetsStr = settings[targetsKey] || '';
  const targets = targetsStr ? targetsStr.split(',') : [];

  // 해당 카테고리의 bonusRanges
  const bonusRanges = (settings.bonusRanges || []).filter(r => r.bonusCategory === bonusCategory);

  const handleTargetToggle = value => {
    if (value === 'ALL') {
      if (targets.includes('ALL')) {
        onSettingsChange(targetsKey, '');
      } else {
        onSettingsChange(targetsKey, BONUS_TARGET_OPTIONS.map(o => o.value).join(','));
      }
      return;
    }
    let next;
    if (targets.includes(value)) {
      next = targets.filter(v => v !== value && v !== 'ALL');
    } else {
      next = [...targets.filter(v => v !== 'ALL'), value];
      const allNonAll = BONUS_TARGET_OPTIONS.filter(o => o.value !== 'ALL').map(o => o.value);
      if (allNonAll.every(v => next.includes(v))) {
        next = [...next, 'ALL'];
      }
    }
    onSettingsChange(targetsKey, next.join(','));
  };

  // 마지막 구간은 화면에 상한 입력칸이 없으므로 rangeEnd 를 비워 상한 없음으로 저장한다.
  // (서버는 rangeEnd 가 null 이면 Integer.MAX_VALUE 로 처리한다)
  const withOpenEndedLast = ranges =>
    ranges.map((r, i) => ({ ...r, rangeOrder: i, rangeEnd: i === ranges.length - 1 ? null : r.rangeEnd }));

  const handleAddRange = () => {
    const prevLast = bonusRanges[bonusRanges.length - 1];
    // 직전 마지막 구간은 이제 마지막이 아니므로 상한을 부여한다.
    const prevLastEnd = prevLast ? (prevLast.rangeEnd ?? (prevLast.rangeStart || 0) + 1000000) : 0;
    const newRange = {
      bonusCategory,
      rangeStart: prevLastEnd + 1,
      rangeEnd: null,
      amount: 0,
      unit: settlementType === 'FIXED' ? '원' : '%',
      rangeOrder: bonusRanges.length,
    };
    const otherRanges = (settings.bonusRanges || []).filter(r => r.bonusCategory !== bonusCategory);
    const updated = withOpenEndedLast([
      ...bonusRanges.slice(0, -1),
      ...(prevLast ? [{ ...prevLast, rangeEnd: prevLastEnd }] : []),
      newRange,
    ]);
    onSettingsChange('bonusRanges', [...otherRanges, ...updated]);
  };

  const handleRemoveRange = idx => {
    const allRanges = settings.bonusRanges || [];
    const categoryRanges = allRanges.filter(r => r.bonusCategory === bonusCategory);
    const otherRanges = allRanges.filter(r => r.bonusCategory !== bonusCategory);
    const updated = withOpenEndedLast(categoryRanges.filter((_, i) => i !== idx));
    onSettingsChange('bonusRanges', [...otherRanges, ...updated]);
  };

  const handleRangeChange = (idx, field, value) => {
    const allRanges = settings.bonusRanges || [];
    const categoryRanges = allRanges.filter(r => r.bonusCategory === bonusCategory);
    const otherRanges = allRanges.filter(r => r.bonusCategory !== bonusCategory);
    const updated = [...categoryRanges];

    updated[idx] = { ...updated[idx], [field]: value };
    onSettingsChange('bonusRanges', [...otherRanges, ...updated]);
  };

  return (
    <FieldBlock>
      <FormRow sx={{ alignItems: 'flex-start' }}>
        <FormFieldGroup>
          <ToggleFieldRow>
            <FormFieldLabel>{title}</FormFieldLabel>
            <ToggleSwitch
              checked={enabled}
              onChange={() => onSettingsChange(enabledKey, enabled ? 'N' : 'Y')}
            />
          </ToggleFieldRow>
          <FormFieldDesc>{desc}</FormFieldDesc>
        </FormFieldGroup>

        {enabled && (
          <FieldBlock sx={{ flex: 1, gap: '16px' }}>
            {/* 정산 방식 */}
            <RadioRow sx={{ justifyContent: 'flex-start' }}>
              <LessonLabel>{t('정산 방식')}</LessonLabel>
              <RadioItem onClick={() => onSettingsChange(typeKey, 'FIXED')}>
                <RadioCircle isSelected={settlementType === 'FIXED'}>
                  {settlementType === 'FIXED' && <RadioDot />}
                </RadioCircle>
                <RadioLabel>{t('정액제')}</RadioLabel>
              </RadioItem>
              <RadioItem onClick={() => onSettingsChange(typeKey, 'RATE')}>
                <RadioCircle isSelected={settlementType === 'RATE'}>
                  {settlementType === 'RATE' && <RadioDot />}
                </RadioCircle>
                <RadioLabel>{t('정률제')}</RadioLabel>
              </RadioItem>
            </RadioRow>
            <DisabledNote>
              {t('※ 정산 방식은 새로 추가하는 구간의 기본 단위(원/%)만 정합니다. 실제 정액·정률은 각 구간에서 고른 단위로 결정됩니다.')}
            </DisabledNote>

            <Divider />

            {/* 부가세 · 성과금 기준 — 성과급(개인 / 직급 / 지점) 전부 급여방식과 무관하게 매출 기준이라
                이 기준액에 반영된다. 판매금액이면 정가, 결제금액이면 실제 결제액을 쓰고,
                부가세 미포함이면 공급가액(×10÷11)으로 환산한다. */}
            <Box>
              <RadioRow sx={{ justifyContent: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LessonLabel>{t('부가세')}</LessonLabel>
                  <HelpTooltip title={"포함: 매출 금액을 그대로 사용하여 계산\n미포함: 매출 금액에서 부가세(10%)를 제외한 금액으로 계산\n\n예) 매출 110만원\n포함 → 110만원 기준\n미포함 → 100만원 기준"} />
                </Box>
                <RadioItem onClick={() => onSettingsChange(vatKey, 'Y')}>
                  <RadioCircle isSelected={vatIncluded === 'Y'}>
                    {vatIncluded === 'Y' && <RadioDot />}
                  </RadioCircle>
                  <RadioLabel>{t('포함')}</RadioLabel>
                </RadioItem>
                <RadioItem onClick={() => onSettingsChange(vatKey, 'N')}>
                  <RadioCircle isSelected={vatIncluded !== 'Y'}>
                    {vatIncluded !== 'Y' && <RadioDot />}
                  </RadioCircle>
                  <RadioLabel>{t('미포함')}</RadioLabel>
                </RadioItem>
              </RadioRow>

              <RadioRow sx={{ justifyContent: 'flex-start', mt: '12px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LessonLabel>{t('성과금 기준')}</LessonLabel>
                </Box>
                <RadioItem onClick={() => onSettingsChange(basisKey, 'SALE_PRICE')}>
                  <RadioCircle isSelected={basis === 'SALE_PRICE'}>
                    {basis === 'SALE_PRICE' && <RadioDot />}
                  </RadioCircle>
                  <RadioLabel>{t('판매금액')}</RadioLabel>
                </RadioItem>
                <RadioItem onClick={() => onSettingsChange(basisKey, 'PAID_PRICE')}>
                  <RadioCircle isSelected={basis === 'PAID_PRICE'}>
                    {basis === 'PAID_PRICE' && <RadioDot />}
                  </RadioCircle>
                  <RadioLabel>{t('결제금액')}</RadioLabel>
                </RadioItem>
              </RadioRow>
            </Box>
            <NoteText>
              {t('※ 부가세 미포함을 고르면 공급가액(매출 ÷ 1.1) 기준으로 산정됩니다')}
            </NoteText>

            <Divider />

            {/* 산정 대상 — 하나도 안 고르면 백엔드가 0원으로 계산하므로 최소 1개는 강제한다 */}
            {(() => {
              const hasTargetsError = validationError?.field === `${bonusCategory}_bonusTargets` && targets.length === 0;
              return (
                <ErrorFieldWrapper hasError={hasTargetsError} data-field={`${bonusCategory}_bonusTargets`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LessonLabel>{t('산정 대상')}</LessonLabel>
                      <HelpTooltip title={"성과급 구간 판단 시 어떤 수업/상품의 수당을 합산할지 선택합니다.\n\n전체: 모든 항목 합산\n개별 선택 시 선택한 항목의 수당만 합산하여 구간을 판단합니다."} />
                    </Box>
                    {BONUS_TARGET_OPTIONS.map(opt => (
                      <CheckRow key={opt.value} sx={{ cursor: 'pointer' }} onClick={() => handleTargetToggle(opt.value)}>
                        <Checkbox
                          checked={targets.includes(opt.value)}
                          sx={{ padding: 0, color: '#D9D9D9', '&.Mui-checked': { color: '#4AB3BC' } }}
                          size="small"
                        />
                        <CheckLabel>{t(opt.label)}</CheckLabel>
                      </CheckRow>
                    ))}
                  </Box>
                </ErrorFieldWrapper>
              );
            })()}
            {validationError?.field === `${bonusCategory}_bonusTargets` && targets.length === 0 && (
              <ErrorText>{t('산정 대상을 하나 이상 선택해주세요')}</ErrorText>
            )}

            <Divider />

            {/* 구간별 설정 */}
            <Box data-field={`${bonusCategory}_bonusRange`} sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <LessonLabel>{t('구간 설정')}</LessonLabel>
              {bonusRanges.length === 0 && (
                <EmptyRangeHint>{t('설정된 구간이 없습니다. 구간을 추가해주세요')}</EmptyRangeHint>
              )}
              {bonusRanges.map((range, idx) => {
                const isLast = idx === bonusRanges.length - 1;
                const hasAmountError = validationError?.field === `${bonusCategory}_bonusRangeAmount` && !range.amount;
                const hasPercentError = validationError?.field === `${bonusCategory}_bonusRangePercent`
                  && range.unit === '%' && range.amount > 100;
                const hasRangeError = validationError?.field === `${bonusCategory}_bonusRangeInvalid`
                  && range.rangeEnd != null && range.rangeStart > range.rangeEnd;
                return (
                  <Box key={idx}>
                    <RangeRow>
                      <ErrorFieldWrapper hasError={hasRangeError}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <CommaInput
                            inputWidth="120px"
                            value={range.rangeStart || 0}
                            onChange={val => handleRangeChange(idx, 'rangeStart', val)}
                          />
                          <InputUnit>{t('원 이상')}</InputUnit>
                          {!isLast && (
                            <>
                              <CommaInput
                                inputWidth="140px"
                                value={range.rangeEnd || 0}
                                onChange={val => handleRangeChange(idx, 'rangeEnd', val)}
                              />
                              <InputUnit>{t('원 이하 시')}</InputUnit>
                            </>
                          )}
                          {isLast && <InputUnit>{t('시')}</InputUnit>}
                        </Box>
                      </ErrorFieldWrapper>
                      <ErrorFieldWrapper
                        hasError={hasAmountError || hasPercentError}
                        data-field={hasPercentError ? `${bonusCategory}_bonusRangePercent` : `${bonusCategory}_bonusRangeAmount`}
                      >
                        <CommaInput
                          inputWidth="140px"
                          value={range.amount || 0}
                          onChange={val => handleRangeChange(idx, 'amount', val)}
                        />
                      </ErrorFieldWrapper>
                      <UnitSelect value={range.unit || '원'} onChange={e => handleRangeChange(idx, 'unit', e.target.value)}>
                        <option value="원">원</option>
                        <option value="%">%</option>
                      </UnitSelect>
                      <OverflowLabel>{t('추가 지급')}</OverflowLabel>
                      <RemoveCircleOutlineIcon
                        sx={{ fontSize: 22, color: '#CF1322', cursor: 'pointer', flexShrink: 0 }}
                        onClick={() => handleRemoveRange(idx)}
                      />
                    </RangeRow>
                    {hasAmountError && <ErrorText>{t('구간 금액을 입력해주세요')}</ErrorText>}
                    {hasPercentError && <ErrorText>{t('구간 요율은 0~100% 사이로 입력해주세요')}</ErrorText>}
                    {hasRangeError && <ErrorText>{t('구간 범위가 올바르지 않습니다')}</ErrorText>}
                  </Box>
                );
              })}

              <AddRangeButton onClick={handleAddRange}>
                <AddCircleOutlineIcon sx={{ fontSize: 24, color: '#4AB3BC' }} />
                <AddRangeText>{t('구간 추가')}</AddRangeText>
              </AddRangeButton>
            </Box>
          </FieldBlock>
        )}
      </FormRow>
    </FieldBlock>
  );
};

const SalarySettingPerformance = ({ settings, onSettingsChange, validationError }) => {
  const { t } = useTranslation();

  // 개인 성과급의 기준액은 급여방식과 무관하게 항상 본인이 판매담당자로 등록한 매출이다
  // (SalarySettlementService.bonusForPersonal → getSalesAmountByRegisteredBy). 예전엔
  // 횟수제만 "본인 수업수당" 기준이라고 안내했는데 실제 계산 로직과 달라 틀린 안내였다.
  const personalBonusDesc = t('본인이 판매담당자로 등록한 매출을 기반으로 성과급 산정');

  return (
    <Card>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <SectionTitle>{t('성과급')}</SectionTitle>
        <HelpTooltip title={"설정한 구간에 기준액이 해당하면 성과급을 지급합니다.\n\n기준액은 종류마다 다릅니다.\n개인: 본인이 판매담당자로 등록한 매출\n직급: 같은 직급 구성원의 판매 실적(매출) 합계\n지점: 지점 전체 매출\n\n지급 방식은 각 구간에서 고른 단위로 결정됩니다 — 원이면 고정 금액, %면 기준액의 비율."} />
      </Box>

      <SectionContent sx={{ mt: '24px' }}>
        <BonusSettingItem
          title={t('개인 성과급')}
          desc={personalBonusDesc}
          enabledKey="personalBonusEnabled"
          typeKey="personalBonusType"
          thresholdKey="personalBonusThreshold"
          amountKey="personalBonusAmount"
          vatKey="personalBonusVatIncluded"
          basisKey="personalBonusBasis"
          targetsKey="personalBonusTargets"
          bonusCategory="PERSONAL"
          settings={settings}
          onSettingsChange={onSettingsChange}
          validationError={validationError}
        />

        <Divider />

        <BonusSettingItem
          title={t('직급 성과급')}
          desc={t('같은 직급 구성원의 총 매출을 기반으로 성과급 산정')}
          enabledKey="positionBonusEnabled"
          typeKey="positionBonusType"
          thresholdKey="positionBonusThreshold"
          amountKey="positionBonusAmount"
          vatKey="positionBonusVatIncluded"
          basisKey="positionBonusBasis"
          targetsKey="positionBonusTargets"
          bonusCategory="POSITION"
          settings={settings}
          onSettingsChange={onSettingsChange}
          validationError={validationError}
        />

        <Divider />

        <BonusSettingItem
          title={t('지점 성과급')}
          desc={t('지점 전체 매출을 기반으로 성과급 산정')}
          enabledKey="branchBonusEnabled"
          typeKey="branchBonusType"
          thresholdKey="branchBonusThreshold"
          amountKey="branchBonusAmount"
          vatKey="branchBonusVatIncluded"
          basisKey="branchBonusBasis"
          targetsKey="branchBonusTargets"
          bonusCategory="BRANCH"
          settings={settings}
          onSettingsChange={onSettingsChange}
          validationError={validationError}
        />
      </SectionContent>
    </Card>
  );
};

// ============================================
// 비과세 항목 설정 섹션
// ============================================
const TaxExemptItem = ({ title, desc, value, onChange, enabled, onToggle, hasError, errorMessage, fieldName }) => {
  const { t } = useTranslation();

  return (
    <FieldBlock>
      <FormRow>
        <FormFieldGroup>
          <ToggleFieldRow>
            <FormFieldLabel>{title}</FormFieldLabel>
            <ToggleSwitch checked={enabled} onChange={onToggle} />
          </ToggleFieldRow>
          <FormFieldDesc>{desc}</FormFieldDesc>
        </FormFieldGroup>
        {enabled && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ErrorFieldWrapper hasError={hasError} data-field={fieldName}>
                <CommaInput
                  inputWidth="180px"
                  value={value}
                  onChange={onChange}
                />
              </ErrorFieldWrapper>
              <InputUnit>{t('원')}</InputUnit>
            </Box>
            {hasError && <ErrorText>{errorMessage || t('금액을 입력해주세요')}</ErrorText>}
          </Box>
        )}
      </FormRow>
    </FieldBlock>
  );
};

const SalarySettingTaxExempt = ({ settings, onSettingsChange, validationError }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <SectionTitleRow>
        <SectionTitle>{t('비과세 항목 설정')}</SectionTitle>
        <NoteText>{t('※ 비과세 한도 월 20만원')}</NoteText>
      </SectionTitleRow>

      <SectionContent sx={{ mt: '24px' }}>
        <TaxExemptItem
          title={t('식대(식사 보조금)')}
          desc={t('비과세 한도 월 20만원')}
          value={settings?.mealAllowance}
          onChange={val => onSettingsChange('mealAllowance', val)}
          enabled={settings?.mealAllowance > 0 || settings?.mealAllowanceEnabled}
          onToggle={() => {
            const next = !(settings?.mealAllowance > 0 || settings?.mealAllowanceEnabled);
            onSettingsChange('mealAllowanceEnabled', next);
            if (!next) onSettingsChange('mealAllowance', 0);
          }}
          hasError={validationError?.field === 'mealAllowance'}
          errorMessage={validationError?.field === 'mealAllowance' ? validationError.message : ''}
          fieldName="mealAllowance"
        />

        <Divider />

        <TaxExemptItem
          title={t('교통비(여비 교통비)')}
          desc={t('비과세 한도 월 20만원')}
          value={settings?.transportAllowance}
          onChange={val => onSettingsChange('transportAllowance', val)}
          enabled={settings?.transportAllowance > 0 || settings?.transportAllowanceEnabled}
          onToggle={() => {
            const next = !(settings?.transportAllowance > 0 || settings?.transportAllowanceEnabled);
            onSettingsChange('transportAllowanceEnabled', next);
            if (!next) onSettingsChange('transportAllowance', 0);
          }}
          hasError={validationError?.field === 'transportAllowance'}
          errorMessage={validationError?.field === 'transportAllowance' ? validationError.message : ''}
          fieldName="transportAllowance"
        />

        <Divider />

        <TaxExemptItem
          title={t('차량 유지비(운전 보조금)')}
          desc={t('비과세 한도 월 20만원')}
          value={settings?.vehicleAllowance}
          onChange={val => onSettingsChange('vehicleAllowance', val)}
          enabled={settings?.vehicleAllowance > 0 || settings?.vehicleAllowanceEnabled}
          onToggle={() => {
            const next = !(settings?.vehicleAllowance > 0 || settings?.vehicleAllowanceEnabled);
            onSettingsChange('vehicleAllowanceEnabled', next);
            if (!next) onSettingsChange('vehicleAllowance', 0);
          }}
          hasError={validationError?.field === 'vehicleAllowance'}
          errorMessage={validationError?.field === 'vehicleAllowance' ? validationError.message : ''}
          fieldName="vehicleAllowance"
        />
      </SectionContent>
    </Card>
  );
};

export { SalarySettingOverflow, SalarySettingPerformance, SalarySettingTaxExempt };
